import React, { Component } from 'react';
import PropTypes from 'prop-types';
import deepEqual from 'deep-equal';
import classNames from 'classnames';
import { Field, propTypes as reduxFormPropTypes } from 'redux-form';
import CSSTransitionReplace from 'react-css-transition-replace';
import { CSSTransitionGroup } from 'react-transition-group';
import { FormattedMessage } from 'react-intl';
import CancelIcon from 'react-material-icons/icons/navigation/cancel';
import AddIcon from 'react-material-icons/icons/content/add-circle-outline';
import getValidators from '@chama/utils/functions/validators';
import { currencyToFloat, formatPrice } from '@chama/utils/functions/currency';
import productLabel from '@chama/utils/labels/products';
import Modal from '../Modal';
import Button from '../Button';
import Spinner from '../Spinner';
import Table from '../Table';
import RFTextField from '../ReduxFormTextField';
import Card from '../CardOld';

import './index.css';

const propTypes = {
  ...reduxFormPropTypes,
  onEdit: PropTypes.func.isRequired,
  onPriceChange: PropTypes.func.isRequired,
  removeProductFromDealer: PropTypes.func.isRequired,
  dealerId: PropTypes.string.isRequired,
  products: PropTypes.array.isRequired,
  hideAddButton: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
};

const defaultProps = {
  isDisabled: false,
};

const MULTIMARCAS = 'MULTIMARCAS';
const tableStyle = { borderColor: '#ebebeb' };

export const EDIT_CONTENT_ID = 'CHANGE_PRICE_EDIT_CONTENT';
export const DEFAULT_CONTENT_ID = 'CHANGE_PRICE_DEFAULT_CONTENT';
export const EDIT_BTN_ID = 'CHANGE_PRICE_EDIT_BTN';
export const CANCEL_BTN_ID = 'CHANGE_PRICE_CANCEL_BTN';

class ManageProducts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditing: false,
      isLoading: false,
      currentProduct: null,
      priceFields: [],
    };
    this.validators = getValidators('pt-BR');
  }

  componentWillMount() {
    const { autofill, products } = this.props;
    const productAutofillField = product => {
      autofill(`${product.id}.price`, this.formatPriceToString(product.price));
    };

    this.generateFieldsForProducts(products);
    products.forEach(productAutofillField);
  }

  componentWillReceiveProps(newProps) {
    const { products, dealerId } = this.props;
    const hasProductsChanged = !deepEqual(newProps.products, products);
    const dealerChanged = newProps.dealerId !== dealerId;

    if (hasProductsChanged || dealerChanged) {
      this.doneEditing();
      this.generateFieldsForProducts(newProps.products);
    }
  }

  componentDidUpdate(prevProps) {
    const { products } = this.props;
    const hasProductsChanged = !deepEqual(products, prevProps.products);

    if (hasProductsChanged) {
      this.setProductsFieldValues(products);
    }
  }

  handleRef = refName => {
    return ref => {
      this[refName] = ref;
    };
  };

  formatPriceToString = price => {
    if (price) {
      return price.toFixed(2).replace('.', ',');
    }

    return price;
  };

  generateFieldsForProducts = products => {
    const intoPriceReduxFormFields = (accum, product) => {
      return {
        ...accum,
        [product.id]: fieldInfo => (
          <RFTextField
            {...fieldInfo}
            showValid={false}
            showInlineValidation={false}
            textFieldProps={{
              currency: true,
              name: fieldInfo,
              placeholder: 'R$ XX,XX',
              disabled: fieldInfo.disabled,
              noBorderRadius: true,
            }}
          />
        ),
      };
    };

    this.setState({ priceFields: products.reduce(intoPriceReduxFormFields, {}) });
  };

  setProductsFieldValues = (products = []) => {
    const { change } = this.props;
    const productChangeFieldValue = product => {
      change(`${product.id}.price`, this.formatPriceToString(product.price));
    };

    products.forEach(productChangeFieldValue);
  };

  editInfo = () => {
    const { onEdit } = this.props;

    onEdit(true);
    this.setState({ isEditing: true });
  };

  cancelEdit = () => {
    const { products } = this.props;

    this.setProductsFieldValues(products);
    this.doneEditing();
  };

  doneEditing = () => {
    const { onEdit } = this.props;

    onEdit(false);

    this.setState({ isEditing: false, isLoading: false });
  };

  getProducts = () => {
    const { products } = this.props;

    if (products.length >= 2) {
      const removeMultimarcas = product => product.brand !== MULTIMARCAS;

      return products.filter(removeMultimarcas);
    }

    return products;
  };

  getDisableRemoveButton = () => {
    const products = this.getProducts();

    return products && products.length === 1;
  };

  onFormSubmit = fields => {
    const { products, isDisabled } = this.props;
    const intoNewData = product => {
      const { id } = product;

      return {
        ...product,
        price: currencyToFloat(fields[id].price),
      };
    };

    const newProductsData = products.map(intoNewData);
    if (!isDisabled) {
      this.changeProductsData(newProductsData);
    }
  };

  changeProductsData = newProductsData => {
    const { products, onPriceChange } = this.props;
    const oldProducts = { products };
    const newProducts = { products: newProductsData };

    this.setState({ isLoading: true });

    onPriceChange(oldProducts, newProducts, 'bottlePrice')
      .then(this.doneEditing)
      .catch(this.cancelEdit);
  };

  removeProductFromDealer = () => {
    const { removeProductFromDealer } = this.props;
    const { currentProduct } = this.state;

    this.setState({ isLoading: true });

    removeProductFromDealer(currentProduct.id).catch(this.cancelEdit);
  };

  onRemoveBtnClick = currentProduct => {
    const disableRemoveButton = this.getDisableRemoveButton();

    if (!disableRemoveButton) {
      this.setState({ currentProduct });
      this.removeProduct.show();
    }
  };

  renderLoadingSpinner = () => {
    const { isLoading } = this.state;

    if (isLoading) {
      return (
        <div className="ManageProducts__loading_wrapper" key="loading">
          <Spinner fill>
            <FormattedMessage id="saving" defaultMessage="Saving" />
          </Spinner>
        </div>
      );
    }

    return null;
  };

  renderValidationErrors = () => {
    const { error, activeField } = this.props;

    if (error && error.length && !activeField) {
      return <div className="ManageProducts__error_msg">{error}</div>;
    }

    return null;
  };

  renderReadOnlyTableBody = () => {
    const products = this.getProducts();

    const intoTableRows = product => {
      return (
        <Table.Row key={product.id}>
          <Table.Cell>Gás</Table.Cell>

          <Table.Cell>{product.brand}</Table.Cell>

          <Table.Cell>{productLabel[product.type] || product.type}</Table.Cell>

          <Table.Cell>{formatPrice(product.price)}</Table.Cell>
        </Table.Row>
      );
    };

    return products.map(intoTableRows);
  };

  renderFieldsTableBody = () => {
    const disableRemoveButton = this.getDisableRemoveButton();
    const products = this.getProducts();
    const removeBtnClass = classNames('ManageProducts__remove_btn', {
      'ManageProducts__remove_btn--disabled': disableRemoveButton,
    });
    const ableToRemoveProducts = this.isAbleToRemoveProducts();
    const intoTableRows = product => {
      const { id } = product;
      const { priceFields } = this.state;
      const reduxFormField = priceFields[id];

      if (reduxFormField) {
        return (
          <Table.Row key={id}>
            <Table.Cell>Gás</Table.Cell>

            <Table.Cell>{product.brand}</Table.Cell>

            <Table.Cell>{productLabel[product.type] || product.type}</Table.Cell>

            <Table.Cell noPadding>
              <Field
                name={`${id}.price`}
                component={reduxFormField}
                validate={[this.validators.required, this.validators.cylinderPrice]}
              />
            </Table.Cell>
            {ableToRemoveProducts && (
              <Table.Cell>
                <CancelIcon
                  className={removeBtnClass}
                  onClick={() => this.onRemoveBtnClick(product)}
                />
              </Table.Cell>
            )}
          </Table.Row>
        );
      }

      return null;
    };

    return products.map(intoTableRows);
  };

  renderTableHeader = () => {
    return (
      <Table.Header>
        <Table.Row>
          <Table.Cell>
            <span className="ManageProducts__header_text">
              <FormattedMessage id="product" defaultMessage="Product" />
            </span>
          </Table.Cell>

          <Table.Cell>
            <span className="ManageProducts__header_text">
              <FormattedMessage id="brand" defaultMessage="Brand" />
            </span>
          </Table.Cell>

          <Table.Cell>
            <span className="ManageProducts__header_text">
              <FormattedMessage id="type" defaultMessage="Type" />
            </span>
          </Table.Cell>

          <Table.Cell>
            <span className="ManageProducts__header_text">
              <FormattedMessage id="price" defaultMessage="Price" />
            </span>
          </Table.Cell>
        </Table.Row>
      </Table.Header>
    );
  };

  isAbleToAddProducts = () => {
    const { products, hideAddButton } = this.props;
    return products.length === 0 || !hideAddButton;
  };

  isAbleToRemoveProducts = () => {
    const { representativeBrand } = this.props;
    return representativeBrand === MULTIMARCAS;
  };

  renderCardBodyContent = () => {
    const { isEditing } = this.state;
    const { handleSubmit, isDisabled, onAddProductClick, products } = this.props;

    const ableToAddProducts = this.isAbleToAddProducts();
    const ableToRemoveProducts = this.isAbleToRemoveProducts();

    if (isEditing) {
      return (
        <form
          key="edit-content"
          autoComplete="off"
          id={EDIT_CONTENT_ID}
          className="ManageProducts__card_body"
          onSubmit={handleSubmit(this.onFormSubmit)}
        >
          <div>
            <Table style={tableStyle}>
              <Table.Header>
                <Table.Row>
                  <Table.Cell>
                    <span className="ManageProducts__header_text">
                      <FormattedMessage id="product" defaultMessage="Product" />
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className="ManageProducts__header_text">
                      <FormattedMessage id="brand" defaultMessage="Brand" />
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className="ManageProducts__header_text">
                      <FormattedMessage id="type" defaultMessage="Type" />
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className="ManageProducts__header_text">
                      <FormattedMessage id="price" defaultMessage="Price" />
                    </span>
                  </Table.Cell>

                  {ableToRemoveProducts && (
                    <Table.Cell>
                      <span className="ManageProducts__header_text">
                        <FormattedMessage id="remove" defaultMessage="Remove" />
                      </span>
                    </Table.Cell>
                  )}
                </Table.Row>
              </Table.Header>

              <Table.Body>{this.renderFieldsTableBody()}</Table.Body>
            </Table>
          </div>

          {ableToAddProducts && (
            <div className="ManageProducts__add_product">
              <div onClick={onAddProductClick}>
                <FormattedMessage id="add_product" defaultMessage="Add product" />
                <AddIcon className="ManageProducts__add_product_icon" />
              </div>
            </div>
          )}

          <div className="ManageProducts__error_msg_wrapper">{this.renderValidationErrors()}</div>

          <div className="ManageProducts__btns">
            <div className="ManageProducts__btn_wrapper">
              <Button id={CANCEL_BTN_ID} variant="secondary" onClick={this.cancelEdit} fullWidth>
                <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
              </Button>
            </div>

            <div className="ManageProducts__btn_wrapper">
              <Button type="submit" disabled={isDisabled} fullWidth>
                <FormattedMessage id="admin.shopinfo.save" defaultMessage="Save" />
              </Button>
            </div>
          </div>
        </form>
      );
    }

    const table = (
      <Table style={tableStyle}>
        <Table.Header>
          <Table.Row>
            <Table.Cell>
              <span className="ManageProducts__header_text">
                <FormattedMessage id="product" defaultMessage="Product" />
              </span>
            </Table.Cell>

            <Table.Cell>
              <span className="ManageProducts__header_text">
                <FormattedMessage id="brand" defaultMessage="Brand" />
              </span>
            </Table.Cell>

            <Table.Cell>
              <span className="ManageProducts__header_text">
                <FormattedMessage id="type" defaultMessage="Type" />
              </span>
            </Table.Cell>

            <Table.Cell>
              <span className="ManageProducts__header_text">
                <FormattedMessage id="price" defaultMessage="Price" />
              </span>
            </Table.Cell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{this.renderReadOnlyTableBody()}</Table.Body>
      </Table>
    );

    const noProductsMessage = (
      <div className="ManageProducts__no_products">
        <FormattedMessage id="no_products_available" defaultMessage="No products" />
      </div>
    );

    return (
      <div key="default-content" id={DEFAULT_CONTENT_ID} className="ManageProducts__card_body">
        {products.length ? table : noProductsMessage}
        <div className="ManageProducts__btns">
          <div className="ManageProducts__btn_wrapper">
            <Button id={EDIT_BTN_ID} onClick={this.editInfo} fullWidth disabled={isDisabled}>
              <FormattedMessage id="edit" defaultMessage="Edit" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      intl: { formatMessage },
    } = this.props;

    return (
      <Card className="ManageProducts">
        <Card.Header>
          <div>
            <FormattedMessage id="manage_products" defaultMessage="Manage products" />
          </div>
        </Card.Header>
        <Card.Body>
          <CSSTransitionReplace
            transitionName="CSSTG-fade"
            transitionEnterTimeout={375}
            transitionLeaveTimeout={300}
          >
            {this.renderCardBodyContent()}
          </CSSTransitionReplace>
          <CSSTransitionGroup
            transitionName="CSSTG-fade"
            transitionEnterTimeout={375}
            transitionLeaveTimeout={300}
          >
            {this.renderLoadingSpinner()}
          </CSSTransitionGroup>
        </Card.Body>
        <Modal
          ref={this.handleRef('removeProduct')}
          title={formatMessage({
            id: 'remove_product_title',
            defaultMessage: 'Remove product',
          })}
          cancelButtonText={formatMessage({
            id: 'general.cancel',
            defaultMessage: 'Cancel',
          })}
          confirmButtonText={formatMessage({
            id: 'remove',
            defaultMessage: 'Remove',
          })}
          onConfirm={this.removeProductFromDealer}
        >
          <div>
            <FormattedMessage
              id="remove_product_body"
              defaultMessage="Are you sure you want to remove this product?"
            />
          </div>
        </Modal>
      </Card>
    );
  }
}

ManageProducts.propTypes = propTypes;
ManageProducts.defaultProps = defaultProps;

export default ManageProducts;



// WEBPACK FOOTER //
// ../ui-components/components/ManageProducts/index.jsx